// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, SectionComponent } from "@components";
import { compose } from "redux";
import type { State } from "@store/reducers";
import { formatDateTime } from "@transformers/date";
import type {
  EmployeeAbsenceAdditionalInfo,
  EditAdditionalInfoModal as EditAdditionalInfoModalType,
} from "@modules/shared/reducer/employee-absences/types";
import actionCreators from "@modules/shared/actionCreator";
import {
  getAdditionalInfo,
  getEditAdditionalInfoModal,
} from "@modules/shared/selectors/employee-absences";
import { EditAdditionalInfoModal } from "@modules/shared/components";
import { formatQuestionAnswerResponse } from "@modules/shared/utils/question-answer-response";

type OwnProps = $Exact<{
  id: string,
  clientId: ?number,
  employeeId: number,
  absenceId: number,
  canEdit?: boolean,
}>;

type PropsFromState = $Exact<{
  data: Array<EmployeeAbsenceAdditionalInfo>,
  editAdditionalInfoModal: EditAdditionalInfoModalType,
}>;

type PropsFromDispatch = $Exact<{
  getAdditionalInfo: typeof actionCreators.GetEmployeeAbsenceAdditionalInfo,
  toggleEditAdditionalInfoModal: typeof actionCreators.ToggleEditEmployeeAbsenceAdditionalInfoModal,
  updateAdditionalInfo: typeof actionCreators.UpdateEmployeeAbsenceAdditionalInfo,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
}>;

export class AbsenceDetailsAdditionalInfo extends React.Component<Props, ComponentState> {
  state = {
    isSectionOpen: true,
  };

  componentDidMount() {
    this.props.getAdditionalInfo({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
    });
  }

  showEditAdditionalInfoModal = (additionalInfo: EmployeeAbsenceAdditionalInfo) => () => {
    this.props.toggleEditAdditionalInfoModal({
      visible: true,
      data: additionalInfo,
    });
  };

  closeEditAdditionalInfoModal = () => {
    this.props.toggleEditAdditionalInfoModal({
      visible: false,
    });
  };

  handleUpdateAdditionalInfo = (formData: Object) => {
    this.props.updateAdditionalInfo({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      formData,
      additionalInfo: this.props.editAdditionalInfoModal,
    });
    this.closeEditAdditionalInfoModal();
  };

  get data() {
    return this.props.data.sort((a, b) => a.question.id - b.question.id);
  }

  get columns() {
    const columns = [
      {
        name: this.props.t("ID"),
        key: "id",
        width: "min-content",
      },
      {
        name: this.props.t("QUESTION"),
        key: "question",
        renderCell: ({ row }: { row: EmployeeAbsenceAdditionalInfo }) => row.question.questionText,
        align: "left",
      },
      {
        name: this.props.t("RESPONSE"),
        key: "answer",
        renderCell: ({ row }: { row: EmployeeAbsenceAdditionalInfo }) =>
          formatQuestionAnswerResponse(row.answer, this.props.t),
        align: "left",
      },
      {
        name: this.props.t("DATE_CAPTURED"),
        renderCell: ({ row }: { row: EmployeeAbsenceAdditionalInfo }) =>
          formatDateTime(row.createdAt),
        key: "dateCaptured",
        width: "min-content",
      },
    ];

    if (this.props.canEdit) {
      columns.push({
        name: this.props.t("EDIT"),
        key: "actions",
        renderCell: ({ row }: { row: EmployeeAbsenceAdditionalInfo }) => {
          return (
            <span
              className="material-icons pointer"
              onClick={this.showEditAdditionalInfoModal(row)}
            >
              edit
            </span>
          );
        },
        width: "min-content",
      });
    }

    return columns;
  }

  toggleSection = () => {
    this.setState({
      isSectionOpen: !this.state.isSectionOpen,
    });
  };

  render() {
    return (
      <SectionComponent
        id={this.props.id}
        title={<h5>{this.props.t("QUESTIONS")}</h5>}
        wrapperClassName="flex-column"
        collapsable
        t={this.props.t}
        onTitleClick={this.toggleSection}
        isOpen={this.state.isSectionOpen}
        className="gs-container"
      >
        <GSGrid rows={this.data} columns={this.columns} />
        {this.props.editAdditionalInfoModal.data && this.props.editAdditionalInfoModal.visible && (
          <EditAdditionalInfoModal
            additionalInfo={this.props.editAdditionalInfoModal.data}
            onDismissHandler={this.closeEditAdditionalInfoModal}
            onSubmit={this.handleUpdateAdditionalInfo}
            absenceId={this.props.absenceId}
          />
        )}
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  data: getAdditionalInfo(state),
  editAdditionalInfoModal: getEditAdditionalInfoModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAdditionalInfo: actionCreators.GetEmployeeAbsenceAdditionalInfo,
  toggleEditAdditionalInfoModal: actionCreators.ToggleEditEmployeeAbsenceAdditionalInfoModal,
  updateAdditionalInfo: actionCreators.UpdateEmployeeAbsenceAdditionalInfo,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceDetailsAdditionalInfo);
